import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonIcon,
} from "@ionic/react";
import { refresh } from "ionicons/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { AppLifecyclePhase } from "../redux/models/refsixState";
import { getProfile } from "../services/profileService";
import { LoginSession } from "../redux/models/loginSession";
import { addUserProfile } from "../redux/actions/addUserProfile";
import { setCurrentUser } from "../redux/actions/currentUser";
import { SubscriptionService } from "../services/subscriptionService";
import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { oneSignalSetUser } from "../services/OneSignal";
import { AppInit } from "./appInit";
import { isProtectedRoute } from "./routeUtils";
import AuthStackScreen from "./AuthStackScreen";
import TabBarScreens from "./TabBarScreens";
import { prePopulateUsernameFromReceipt } from "../services/prePopulateUsernameFromReceipt";

AppInit();

function Root() {
    const { t } = useTranslation();
    const lifecyclePhase = useSelector(
        (state: RootState) => state.appLifecycle.phase
    );
    const session: LoginSession | undefined = useSelector(
        (state: RootState) => state.auth.session
    );
    const dispatch = useDispatch();
    const [initialPath] = useState(
        () =>
            window.location.pathname +
            window.location.search +
            window.location.hash
    );

    const isAuthenticated =
        lifecyclePhase === AppLifecyclePhase.STARTING_DATABASE ||
        lifecyclePhase === AppLifecyclePhase.READY ||
        (lifecyclePhase === AppLifecyclePhase.SESSION_EXPIRED && !!session) ||
        (lifecyclePhase === AppLifecyclePhase.ERROR && !!session);

    useEffect(() => {
        if (isAuthenticated) {
            // TODO should we track this instance in a provider or something?
            const subService = SubscriptionService.getInstance();
            subService.initialize(true);
            if (session) {
                getProfile(session.user_id)
                    .then(function (profile) {
                        oneSignalSetUser(session.user_id, profile.email);
                        dispatch(setCurrentUser(profile));
                        dispatch(addUserProfile(profile));
                    })
                    .catch((error) =>
                        console.log("Couldn't set profile", error)
                    );
            }
        }
    }, [isAuthenticated, session, dispatch]);

    // Cold-start receipt-driven username pre-fill (REFSIX-732). The
    // ref gate ensures this only fires on the first UNAUTHENTICATED
    // transition (i.e. app boot), not on a later in-session USER_LOGOUT
    // → UNAUTHENTICATED transition — we don't want logout to re-pre-fill
    // the username the user just logged out of. The thunk's own outer
    // try/catch makes it impossible to reject, so this isn't awaited.
    const hasPrePopulatedFromReceipt = useRef(false);
    useEffect(() => {
        if (
            lifecyclePhase === AppLifecyclePhase.UNAUTHENTICATED &&
            !hasPrePopulatedFromReceipt.current
        ) {
            hasPrePopulatedFromReceipt.current = true;
            prePopulateUsernameFromReceipt();
        }
    }, [lifecyclePhase]);

    const errorFallback = (
        <IonCard>
            <IonCardHeader>
                <IonCardTitle>
                    {t("general.errorTryAgain")}
                </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
                <IonButton
                    onClick={() => (window.location.pathname = "/")}
                >
                    <IonIcon slot="icon-only" icon={refresh}></IonIcon>
                </IonButton>
            </IonCardContent>
        </IonCard>
    );

    // Show loading while auth status is being determined (appLifecycle is not
    // persisted, so on reload it starts at INITIALIZING until persist/REHYDRATE
    // fires and the middleware transitions to STARTING_DATABASE or UNAUTHENTICATED)
    if (lifecyclePhase === AppLifecyclePhase.INITIALIZING) {
        return null;
    }

    // If user is authenticated (or session expired but not logged out), show the main app.
    // SESSION_EXPIRED keeps the user in TabBarScreens so SessionExpiredBanner/Modal
    // can handle in-app password-only re-authentication.
    if (isAuthenticated) {
        return (
            <Sentry.ErrorBoundary fallback={errorFallback}>
                <TabBarScreens />
            </Sentry.ErrorBoundary>
        );
    }

    // User is not logged in - check if they need auth for initial path
    const needsAuth = isProtectedRoute(
        new URL(initialPath, window.location.origin).pathname
    );

    return (
        <Sentry.ErrorBoundary fallback={errorFallback}>
            {needsAuth ? (
                <AuthStackScreen targetUrl={initialPath} />
            ) : (
                <AuthStackScreen />
            )}
        </Sentry.ErrorBoundary>
    );
}

export default Root;
